/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import { getLocaleByReference, isExternalUrl } from 'msp-integrations'
import { getParamsFromPath } from '@/utils/utils'
import { getDefaultSite, getSites } from '@/utils/site-utils'
import { urlPartPositions, HOME_HREF } from '@/constants'

/**
 * Generates the URL Template literal (Template string) used to build URLs in the App according
 * the current selected site/locale and the default App URL configuration.
 *
 * @param appConfig Application default configuration.
 * @param siteRef Current selected Site reference. The value can be the Site id or alias.
 * @param localeRef Current selected Locale reference. The value can be the Locale id or alias.
 * @returns {function(*, *, *): string} function providing: path, site and locale generates a URL.
 */
export const createUrlTemplate = (appConfig, siteRef, localeRef) => {
  const {
    site: siteConfig,
    locale: localeConfig,
    showDefaults: showDefaultsConfig
  } = appConfig.url
  const defaultSite = getDefaultSite()
  const sites = getSites()
  const siteAliasOrIdRef =
    sites.find((site) => {
      return site.alias === siteRef || site.id === siteRef
    }) || defaultSite
  const defaultLocale = getLocaleByReference(
    siteAliasOrIdRef,
    siteAliasOrIdRef.l10n.defaultLocale
  )

  const isDefaultSite =
    defaultSite.id === siteRef ||
    (defaultSite.alias && defaultSite.alias === siteRef)
  const isDefaultLocale =
    defaultLocale.id === localeRef ||
    (defaultLocale.alias && defaultLocale.alias === localeRef)

  const querySite =
    (siteConfig === urlPartPositions.QUERY_PARAM && showDefaultsConfig) ||
    (siteConfig === urlPartPositions.QUERY_PARAM &&
      !showDefaultsConfig &&
      !isDefaultSite)
  const queryLocale =
    (localeConfig === urlPartPositions.QUERY_PARAM && showDefaultsConfig) ||
    (localeConfig === urlPartPositions.QUERY_PARAM &&
      !showDefaultsConfig &&
      !isDefaultLocale)

  const isQuery = querySite || queryLocale

  const pathSite =
    (siteConfig === urlPartPositions.PATH && showDefaultsConfig) ||
    (siteConfig === urlPartPositions.PATH &&
      !showDefaultsConfig &&
      !isDefaultSite)
  const pathLocale =
    (localeConfig === urlPartPositions.PATH && showDefaultsConfig) ||
    (localeConfig === urlPartPositions.PATH &&
      !showDefaultsConfig &&
      !isDefaultLocale)

  return (path, site, locale) => {
    if (isExternalUrl(path)) return path

    const isHomeWithDefaultSiteAndLocale =
      path === HOME_HREF &&
      (defaultSite.id === site ||
        (defaultSite.alias && defaultSite.alias === site)) &&
      (defaultLocale.id === locale ||
        (defaultLocale.alias && defaultLocale.alias === locale))

    const sitePath =
      pathSite && site && !isHomeWithDefaultSiteAndLocale ? `/${site}` : ''
    const localePath =
      pathLocale && locale && !isHomeWithDefaultSiteAndLocale
        ? `/${locale}`
        : ''

    const hasQuery =
      isQuery && (site || locale) && !isHomeWithDefaultSiteAndLocale

    let queryString = ''
    if (hasQuery) {
      const searchParams = new URLSearchParams()
      querySite && site && searchParams.append('site', site)
      queryLocale && locale && searchParams.append('locale', locale)
      queryString = `?${searchParams.toString()}`
    }

    const pathCleanOfLocales = path
      .replaceAll(sitePath, '')
      .replaceAll(localePath, '')

    return `${sitePath}${localePath}${pathCleanOfLocales || ''}${queryString}`
  }
}

/**
 * Returns a relative URL for a locale short code.
 * Based on your app configuration, this function will replace your current locale shortCode with a new one
 *
 * @param {String} shortCode - The locale short code.
 * @param {function(*, *, *, *=): string} - Generates a site URL from the provided path, site and locale.
 * @param {string[]} opts.disallowParams - URL parameters to remove
 * @param {Object} opts.location - location object to replace the default `window.location`
 * @returns {String} url - The relative URL for the specific locale.
 */
export const getPathWithLocale = (shortCode, buildUrl, opts = {} as any) => {
  const location = opts.location ? opts.location : window.location
  let { siteRef, localeRef } = getParamsFromPath(
    `${location.pathname}${location.search}`
  )
  let { pathname, search } = location

  // sanitize the site from current url if existing
  if (siteRef) {
    pathname = pathname.replace(`/${siteRef}`, '')
    search = search.replace(`site=${siteRef}`, '')
  }
  // sanitize the locale from current url if existing
  if (localeRef) {
    pathname = pathname.replace(`/${localeRef}`, '')
    search = search.replace(`locale=${localeRef}`, '')
  }
  // remove ending any &
  search = search.replace(/&$/, '')

  const defaultSite = getDefaultSite()

  // Remove query parameters
  const { disallowParams = [] } = opts

  let queryString = new URLSearchParams(`${search}`)

  if (disallowParams.length) {
    disallowParams.forEach((param) => {
      queryString.delete(param)
    })
  }

  // rebuild the url with new locale,
  const newUrl = buildUrl(
    `${pathname}${
      Array.from(queryString).length !== 0 ? `?${queryString}` : ''
    }`,
    // By default, as for home page, when the values of site and locale belongs to the default site,
    // they will be not shown in the url just
    defaultSite.alias || defaultSite.id,
    shortCode
  )
  return newUrl
}

/*
 * Remove site alias and locale from a given url, to be used for "navigate" urls
 *
 * @param {string} pathName - The part of url to have site alias and locale removed from
 * @returns {string} - the path after site alias and locale have been removed
 * @example
 * import {removeSiteLocaleFromPath} from /path/to/util/url
 *
 * removeSiteLocaleFromPath(/RefArch/en-US/account/wishlist)
 * // returns '/account/wishlist'
 */
export const removeSiteLocaleFromPath = (pathName = '') => {
  let { siteRef, localeRef } = getParamsFromPath(pathName)

  // remove the site alias from the current pathName
  if (siteRef) {
    pathName = pathName.replace(new RegExp(`/${siteRef}`, 'g'), '')
  }
  // remove the locale from the current pathName
  if (localeRef) {
    pathName = pathName.replace(new RegExp(`/${localeRef}`, 'g'), '')
  }

  return pathName
}
